// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/EntregaDetalheDialog.tsx
//  Detalhe (read-only) de uma entrega: dados, produtos/conferência,
//  recebimentos e totalizadores — espelha o frm_gerenciamento_entrega desktop.
// ════════════════════════════════════════════════════════════════════════════

import {
  MapPin, ExternalLink, Package, Truck, CheckCircle2, Clock, ClipboardCheck, RotateCcw,
} from 'lucide-react';
import { useEntregaDetalhe, useEntregaConferencia } from '@/features/operacional/hooks/useEntrega';
import { Badge } from '@/components/ui/badge';
import { ProdutoNome } from '@/components/ProdutoNome';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

// ── Helpers ─────────────────────────────────────────────────────────────────

const brl = (v?: number | null) =>
  (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const qtd = (v?: number | null) =>
  (v ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 4 });

const fmtData = (v?: string | null) =>
  v ? new Date(v).toLocaleString('pt-BR') : '—';

function buildMapsUrl(e: { endereco?: string | null; numero?: string | null; bairro?: string | null; cidade?: string | null; uf?: string | null }) {
  const parts = [e.endereco, e.numero, e.bairro, e.cidade, e.uf].filter(Boolean).join(', ');
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(parts)}`;
}

// ── Componente ────────────────────────────────────────────────────────────────

interface EntregaDetalheDialogProps {
  entregaId: number | null;
  onClose: () => void;
}

export function EntregaDetalheDialog({ entregaId, onClose }: EntregaDetalheDialogProps) {
  const { data: e, isLoading } = useEntregaDetalhe(entregaId);
  const {
    conferirProduto, isConferindoProduto,
    fecharConferencia, isFechandoConferencia,
    reabrirConferencia, isReabrindoConferencia,
  } = useEntregaConferencia(entregaId);

  const produtos     = e?.produtos     ?? [];
  const recebimentos = e?.recebimentos ?? [];

  // Conferência só é editável enquanto a entrega não está finalizada nem cancelada.
  const editavel = !!e && !e.isFinalizado && !e.isCancelada;
  const ocupado  = isConferindoProduto || isFechandoConferencia || isReabrindoConferencia;
  const totalConferidos = produtos.filter((p) => p.isConferido > 0).length;

  return (
    <Dialog open={entregaId != null} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Detalhe da Entrega {e ? `nº ${e.numeroEntrega}` : ''}
          </DialogTitle>
        </DialogHeader>

        {isLoading || !e ? (
          <p className="py-8 text-center text-muted-foreground">Carregando...</p>
        ) : (
          <div className="space-y-5 text-sm">
            {/* Dados da entrega */}
            <section className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
              <p><span className="text-muted-foreground">Cliente:</span> <strong>{e.nomeRazao || 'Consumidor final'}</strong></p>
              <p><span className="text-muted-foreground">CPF/CNPJ:</span> {e.cpfCnpj ?? '—'}</p>
              <p><span className="text-muted-foreground">Fone:</span> {e.fone ?? '—'}</p>
              <p><span className="text-muted-foreground">Celular:</span> {e.celular ?? '—'}</p>
              <p><span className="text-muted-foreground">Data Entrega:</span> {new Date(e.dtEntrega).toLocaleDateString('pt-BR')}{e.horaEntrega ? ` ${e.horaEntrega}` : ''}</p>
              <p><span className="text-muted-foreground">Motorista:</span> {e.motorista ?? '—'}</p>
            </section>

            {/* Endereço */}
            <section>
              <h3 className="font-medium mb-1">Endereço de entrega</h3>
              {e.isRetirarLoja ? (
                <p className="text-purple-600 flex items-center gap-1">
                  <MapPin className="h-4 w-4" />Retirada na loja
                </p>
              ) : e.endereco ? (
                <p className="flex items-center gap-2 flex-wrap">
                  <span>
                    {e.endereco}, {e.numero ?? 's/n'}
                    {e.complemento ? ` — ${e.complemento}` : ''} — {e.bairro ?? ''}, {e.cidade ?? ''}/{e.uf ?? ''}
                    {e.cep ? ` — CEP ${e.cep}` : ''}
                  </span>
                  <a
                    href={buildMapsUrl(e)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline text-xs"
                  >
                    <ExternalLink className="h-3 w-3" />Ver no Maps
                  </a>
                </p>
              ) : (
                <p className="text-muted-foreground">—</p>
              )}
            </section>

            {/* Linha do tempo de status */}
            <section className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><Clock className="h-3 w-3" />Movimento: {new Date(e.dtMovimento).toLocaleDateString('pt-BR')}</span>
              <span className="flex items-center gap-1"><Truck className="h-3 w-3" />Saída: {fmtData(e.dtSaida)}</span>
              <span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />Conferência: {fmtData(e.dtConferencia)}</span>
              <span className="flex items-center gap-1"><Package className="h-3 w-3" />Retorno: {fmtData(e.dtRetorno)}</span>
            </section>

            {/* Produtos / conferência (separação com escrita) */}
            <section>
              <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <h3 className="font-medium flex items-center gap-2">
                  Conferência / Separação
                  {produtos.length > 0 && (
                    <span className="text-xs text-muted-foreground font-normal">
                      {totalConferidos}/{produtos.length} conferido(s)
                    </span>
                  )}
                  {e.isConferido && (
                    <Badge className="bg-green-500/10 text-green-600">
                      Conferida{e.dtConferencia ? ` em ${fmtData(e.dtConferencia)}` : ''}
                    </Badge>
                  )}
                </h3>
                {editavel ? (
                  e.isConferido ? (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={ocupado}
                      onClick={() => reabrirConferencia()}
                    >
                      <RotateCcw className="h-4 w-4 mr-1" />Reabrir conferência
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      disabled={ocupado || produtos.length === 0}
                      onClick={() => fecharConferencia({})}
                    >
                      <ClipboardCheck className="h-4 w-4 mr-1" />Fechar conferência
                    </Button>
                  )
                ) : (
                  <Badge variant="outline" className="text-muted-foreground">
                    {e.isCancelada ? 'Cancelada' : 'Finalizada'} — somente leitura
                  </Badge>
                )}
              </div>
              <div className="overflow-x-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10 text-center">Conf.</TableHead>
                      <TableHead>Produto</TableHead>
                      <TableHead className="text-right">Qtd</TableHead>
                      <TableHead className="text-right w-28">Qtd Conf.</TableHead>
                      <TableHead className="text-right">Diverg.</TableHead>
                      <TableHead className="text-right">Vlr Unit.</TableHead>
                      <TableHead className="text-right">Vlr Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {produtos.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-4 text-muted-foreground">
                          Nenhum produto na entrega
                        </TableCell>
                      </TableRow>
                    ) : (
                      produtos.map((p) => {
                        const conferido = p.isConferido > 0;
                        const divergencia = p.quantidade - (conferido ? (p.quantidadeConferida || p.quantidade) : 0);
                        return (
                          <TableRow key={p.id}>
                            <TableCell className="text-center">
                              <Checkbox
                                checked={conferido}
                                disabled={!editavel || ocupado}
                                onCheckedChange={(v) =>
                                  conferirProduto({
                                    itemId: p.id,
                                    data: {
                                      isConferido: v === true,
                                      quantidadeConferida: v === true ? p.quantidade : 0,
                                    },
                                  })
                                }
                              />
                            </TableCell>
                            <TableCell className="font-medium"><ProdutoNome id={p.produtoId} /></TableCell>
                            <TableCell className="text-right">{qtd(p.quantidade)}</TableCell>
                            <TableCell className="text-right">
                              <Input
                                key={`${p.id}-${p.quantidadeConferida}`}
                                type="number"
                                step="0.0001"
                                min="0"
                                disabled={!editavel || ocupado}
                                defaultValue={conferido ? (p.quantidadeConferida || p.quantidade) : 0}
                                className="h-8 text-right"
                                onBlur={(ev) => {
                                  const valor = Number(ev.target.value);
                                  if (Number.isNaN(valor) || valor < 0) return;
                                  const atual = conferido ? (p.quantidadeConferida || p.quantidade) : 0;
                                  if (valor === atual) return;
                                  conferirProduto({
                                    itemId: p.id,
                                    data: { isConferido: valor > 0, quantidadeConferida: valor },
                                  });
                                }}
                              />
                            </TableCell>
                            <TableCell className={`text-right ${divergencia !== 0 ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>
                              {divergencia === 0 ? '—' : qtd(divergencia)}
                            </TableCell>
                            <TableCell className="text-right">{brl(p.valorUnitario)}</TableCell>
                            <TableCell className="text-right">{brl(p.valorTotal)}</TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
              {editavel && !e.isConferido && (
                <p className="text-xs text-muted-foreground mt-1">
                  Marque os itens conferidos e ajuste a quantidade conferida; depois clique em “Fechar conferência” para carimbar a data e recalcular os totalizadores.
                </p>
              )}
            </section>

            {/* Valores recebidos */}
            {recebimentos.length > 0 && (
              <section>
                <h3 className="font-medium mb-2">Valores recebidos</h3>
                <div className="overflow-x-auto rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Forma</TableHead>
                        <TableHead>Data</TableHead>
                        <TableHead className="text-right">Valor</TableHead>
                        <TableHead className="text-center">Recebido</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {recebimentos.map((r) => (
                        <TableRow key={r.id}>
                          <TableCell>{r.formaPagRec || <span className="text-muted-foreground">Forma não cadastrada</span>}</TableCell>
                          <TableCell>{new Date(r.dtMovimento).toLocaleDateString('pt-BR')}</TableCell>
                          <TableCell className="text-right">{brl(r.valor)}</TableCell>
                          <TableCell className="text-center">
                            {r.isRecebido
                              ? <Badge className="bg-green-500/10 text-green-600">Sim</Badge>
                              : <Badge variant="outline">Não</Badge>}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </section>
            )}

            {/* Observação */}
            {e.observacao && (
              <section>
                <h3 className="font-medium mb-1">Observação</h3>
                <p className="whitespace-pre-wrap text-muted-foreground">{e.observacao}</p>
              </section>
            )}

            {/* Totalizadores */}
            <section className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 border-t pt-3">
              <div>
                <p className="text-xs text-muted-foreground">Valor Total Carga</p>
                <p className="font-semibold">{brl(e.valorTotalCarga)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Conferido</p>
                <p className="font-semibold">{brl(e.valorTotalConferido)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Taxa de Entrega</p>
                <p className="font-semibold">{brl(e.taxaEntrega)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Recebido</p>
                <p className="font-semibold">{brl(e.valorRecebido)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Troco</p>
                <p className="font-semibold">{brl(e.troco)}</p>
              </div>
              {(e.qtdDivergencia > 0 || e.valorDivergencia > 0) && (
                <div>
                  <p className="text-xs text-muted-foreground">Divergência</p>
                  <p className="font-semibold text-destructive">{qtd(e.qtdDivergencia)} un · {brl(e.valorDivergencia)}</p>
                </div>
              )}
            </section>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
